import React, { useEffect, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import AliceProjectDetailsForm from './AliceProjectDetailsForm';
import AliceRecommendedEntities from './AliceRecommendedEntities';
import Scrollable from '../../../Shared/Edit/Scrollable';
import AliceRecommendedRelations from './AliceRecommendedRelations';
import { addSelectedDataToProject, createOnlyProject, getEntitiesExceedingAttributeLimitOnCreatingRelations } from './utils';
import useUser from '../../../../Hooks/User/useUser';
import useAliceGPT from '../../../../Hooks/AliceGPT/useAliceGPT';
import * as AliceGPTEvents from '../../../../Context/AliceGPT/AliceGPTContextEvents';
import useGetProjects from '../../../../Hooks/Projects/useGetProjects';
import useFeatureFlags from '../../../../Hooks/FeatureFlag/useFeatureFlags';
import { DEFAULT_PAGE_CONFIGURATION } from './variables';
import { USER_PROFILE_ENTITY } from './variables';
import ShuffleMessage from '../../../Shared/ShuffleMessage/ShuffleMessage';
import { bindShuffleMessageI18nEvents, messagesWhenCreatingProject } from '../../../Shared/ShuffleMessage/MessagesHelper';
import startsWithNumber from '../../../Helpers/Validators/startsWithNumber';
import { CreateProjectRequest } from './RequestTypes';
import { useTranslation } from 'react-i18next';
import { postMessageToRuntime } from '../../../Helpers/Runtime/runtime';
import { getCurrentSubscriptionForOrganisation } from '../../../../API/Payment/Payment';
import { bindI18nEvents as bindAttributeTypeDropdowni18n } from '../../../Attributes/Fields/AttributeTypeField';
import { bindI18nEvents as bindAttributeTypeFieldi18n } from '../../../Shared/Recommendation/AttributeTypeDropdown';
import EditPageConfiguration from './EditPageConfiguration';
import { LanguagesMap } from '../../../OrganizationSettings/LanguagesMap';

function AliceGPTDialog({ organizationId, organizationName, toggleDialog, toggleUpgradeDialog, onCreate }) {

    const [state, dispatch] = useAliceGPT();

    const titles = [
        "Create App",
        "Create App - Alice AI Recommendation",
        "Create App - Alice AI Recommendation",
    ]

    async function handleCreate(request) {
        const project = await createOnlyProject(request);
        if (project._id) {
            await addSelectedDataToProject(
                request.organizationDetails.organizationId, project._id,
                request.entities, request.relations
            );
        }
        onCreate(project);
    }

    return (
        <Dialog
            title={titles[state.step]}
            height={"90%"}
            width={"80%"}
            onClose={toggleDialog}
        >
            <AliceGPTCreateProjectForm
                organizationId={organizationId}
                organizationName={organizationName}
                toggleDialog={toggleDialog}
                toggleUpgradeDialog={toggleUpgradeDialog}
                onCreate={handleCreate}
            />
        </Dialog>
    )
}

export function AliceGPTCreateProjectForm({
    organizationId,
    organizationName,
    isUsedInRuntime,
    defaultLanguage,
    onCreate,
    skipAIAndCreateProject,
    showAIFields = true,
    showLanguageDropdown = false,
    backToAppLibrary,
    showBackButton
}) {

    const { t } = useTranslation([
        "create_project/alice_gpt_dialog",
        "create_project/alice_project_details_form",
        "create_project/alice_recommended_entities",
        "create_project/alice_recommended_attributes_list",
        "create_project/alice_recommended_relations",
        "create_project/edit_page_configurations",
        "linked_entity/recommended_relation_card",
        "common/attribute_type_dropdown",
        "common/attribute_type_field"
    ], { useSuspense: false });

    const [state, dispatch] = useAliceGPT();
    const featureFlags = useFeatureFlags();
    const [userLoading, user, userError] = useUser();
    const [projectsLoading, projects, projectsError] = useGetProjects(organizationId);

    const [creatingProject, setCreatingProject] = useState(false);

    useEffect(() => {
        bindShuffleMessageI18nEvents()
        getCurrentSubscriptionForOrganisation(organizationId)
            .then(subscription => {
                dispatch({
                    type: AliceGPTEvents.SUBSCRIPTION_LOADED,
                    payload: subscription
                })
            })
        bindAttributeTypeDropdowni18n()
        bindAttributeTypeFieldi18n()
    }, []);

    useEffect(() => {
        if (defaultLanguage) {
            const language = LanguagesMap.find(lang => lang.code === defaultLanguage);
            dispatch({
                type: AliceGPTEvents.FORM_UPDATED,
                payload: {
                    ...state.form,
                    language: language
                }
            })
        }
    }, [defaultLanguage])

    function isNextDisabled() {
        switch (state.step) {
            case 0:
                return (
                    !state.form.projectName.trim() ||
                    /[^a-zA-Z0-9 ]/g.test(state.form.projectName) ||
                    isProjectNameUsed() ||
                    entityWithErrorSelected() ||
                    startsWithNumber(state.form.projectName.trim())
                )
            case 1:
                const selectedEntities = state.entities.filter(entity => entity.checked);
                const hasEmptyEntitiesName = selectedEntities.some(entity => entity.name.trim().length === 0);
                const hasEntitiesStartWithNumber = selectedEntities.some(entity => startsWithNumber(entity.name.trim()));
                const hasSameNameAsAnotherEntity = selectedEntities.some((entity, entityIndex) => {
                    if (entity.checked) {
                        const entityName = entity.name.trim().toLowerCase();
                        return selectedEntities.some((e, i) =>
                            e.name.trim().toLowerCase() === entityName &&
                            i !== entityIndex
                        );
                    }
                    return false;
                });
                const entityHasAttributesWithSameName = selectedEntities.some(entity => {
                    if (entity.checked) {
                        const attributes = entity.attributes.filter(attribute => attribute.selected);
                        const attributeNames = attributes.map(attribute => attribute.name.trim().toLowerCase());
                        return attributeNames.some(name => attributeNames.filter(n => n === name).length > 1);
                    }
                    return false;
                });
                const entityWithNoPagesSelected = selectedEntities.some(entity => (
                    Object.values(entity.pageConfiguration).every(value => !value)
                ))
                if (hasSameNameAsAnotherEntity || entityHasAttributesWithSameName || hasEntitiesStartWithNumber || hasEmptyEntitiesName) return true;
                if (entityWithNoPagesSelected) return false;
                return isCreateAppDisabled();
            case 2:
                const selectedRelations = state.relations.filter(relation => relation.selected);
                const selectedEntitiesWithNoPagesSelected = state.entities.some(entity => (
                    entity.checked &&
                    Object.values(entity.pageConfiguration).every(value => !value)
                ));
                if (selectedEntitiesWithNoPagesSelected) return false;
                const relationWithNoRefAttrSelected = state.relations.find(relation => relation.showReferenceAttribute && !relation.referenceAttribute)
                if (relationWithNoRefAttrSelected) {
                    return true;
                }
                const entitiesWithAttributeLimitExceeded = getEntitiesExceedingAttributeLimitOnCreatingRelations(
                    state.entities,
                    state.relations,
                    state.subscription
                )
                if (entitiesWithAttributeLimitExceeded.length > 0) {
                    return true;
                }
                return (
                    state.error ||
                    !state.initialAttributesLoaded ||
                    selectedRelations.length > state.subscription.maxRelationsPerApp
                );
            default:
                return false;
        }
    }

    function isCreateAppDisabled() {
        const selectedEntities = state.entities.filter(entity => entity.checked);
        const isInLoading = selectedEntities.some(entity => entity.attributesLoading);
        if (isInLoading) return true;
        const hasSameNameAsAnotherEntity = selectedEntities.some((entity, entityIndex) => {
            if (entity.checked) {
                const entityName = entity.name.trim().toLowerCase();
                return selectedEntities.some((e, i) =>
                    e.checked &&
                    e.name.trim().toLowerCase() === entityName &&
                    i !== entityIndex
                );
            }
            return false;
        });
        const hasEmptyEntitiesName = selectedEntities.some(entity => entity.name.trim().length === 0);
        const hasEntitiesStartWithNumber = selectedEntities.some(entity => startsWithNumber(entity.name.trim()));
        const entityHasAttributesWithSameName = selectedEntities.some(entity => {
            if (entity.checked) {
                const attributes = entity.attributes.filter(attribute => attribute.selected);
                const attributeNames = attributes.map(attribute => attribute.name.trim().toLowerCase());
                return attributeNames.some(name => attributeNames.filter(n => n === name).length > 1);
            }
            return false;
        });
        const entityWithMoreThanSubscriptionAttributes = selectedEntities.some(entity =>
            entity.attributes.filter(attribute => attribute.selected).length > state.subscription.maxAttributesPerApp
        );
        const entityWithNoAttributesSelected = selectedEntities.some(entity => entity.attributes.filter(attribute => attribute.selected).length === 0);
        const entityWithMoreThanOneStateAttribute = selectedEntities.some(entity => entity.attributes.filter(attribute => attribute.selected && attribute.type === "State Field").length > 1);
        const entityWithPicklistOrStateWith0AttributeSelected = selectedEntities.some(entity => (
            entity.attributes.find(attribute => (
                attribute.selected &&
                (attribute.type === "Picklist String" || attribute.type === "State Field") &&
                (!attribute.picklist || attribute.picklist.length === 0)
            ))
        ))
        const entityWithAttributesNameWithError = selectedEntities.some(entity => (
            entity.attributes.some(attribute => (
                attribute.selected &&
                (attribute.name.trim().length === 0 || /^\d/g.test(attribute.name))
            ))
        ))
        const entityWithSameAttributesName = selectedEntities.some(entity => {
            if (entity.checked) {
                const attributes = entity.attributes.filter(attribute => attribute.selected);
                const attributeNames = attributes.map(attribute => attribute.name.trim().toLowerCase());
                return attributeNames.some(name => attributeNames.filter(n => n === name).length > 1);
            }
            return false;
        });
        const hasEntityWithMoreThan30Chars = selectedEntities.some(entity => entity.name.trim().length > 30);
        const entityHasAttributesWithMoreThan30Chars = selectedEntities.some(entity => {
            if (entity.checked) {
                const attributes = entity.attributes.filter(attribute => attribute.selected);
                const attributeNames = attributes.map(attribute => attribute.name.trim());
                return attributeNames.some(name => name.length > 30);
            }
            return false;
        });

        const entityWithNoPagesSelected = selectedEntities.some(entity => (
            Object.values(entity.pageConfiguration).every(value => !value)
        ))
        if (state.step === 3 && state.isPageConfigurationLoading) {
            return true;
        }

        if (state.step === 2) {
            const entitiesWithAttributeLimitExceeded = getEntitiesExceedingAttributeLimitOnCreatingRelations(
                state.entities,
                state.relations,
                state.subscription
            )
            if (entitiesWithAttributeLimitExceeded.length > 0) {
                return true;
            }
        }
        return (
            selectedEntities.length > state.subscription.maxEntitiesPerApp ||
            entityWithMoreThanSubscriptionAttributes ||
            entityWithNoAttributesSelected ||
            entityWithMoreThanOneStateAttribute ||
            entityWithPicklistOrStateWith0AttributeSelected ||
            hasSameNameAsAnotherEntity ||
            entityWithSameAttributesName ||
            hasEmptyEntitiesName ||
            hasEntitiesStartWithNumber ||
            entityHasAttributesWithSameName ||
            entityWithAttributesNameWithError ||
            entityWithNoPagesSelected ||
            hasEntityWithMoreThan30Chars ||
            entityHasAttributesWithMoreThan30Chars
        )
    }

    function isProjectNameUsed() {
        return projects.some(project => project.name.trim().toLowerCase() === state.form.projectName.trim().toLowerCase());
    }

    function entityWithErrorSelected() {
        return state.entities.some(entity => entity.hasAttributeLoadingError && entity.checked);
    }

    function handleNext() {
        if (state.step === 0 && state.form.projectName.trim()) {
            postMessageToRuntime({
                eventType: "PROJECT_NAME_CHANGED",
                value: state.form.projectName
            })
        } else if (state.step === 1) {
            const numSelectedEntities = state.entities.filter(entity => entity.checked).length;
            if (numSelectedEntities <= 2) {
                dispatch({ type: AliceGPTEvents.NEXT });
            }
        } else if (state.step === 2) {
            if (!state.isPageConfigurationLoading) {
                dispatch({
                    type: AliceGPTEvents.TOGGLE_DISPLAY_CONFIGURATION_LOADED
                })
            }
        }
        dispatch({ type: AliceGPTEvents.NEXT });
    }

    function handleBack() {
        if (state.step === 3) {
            const numSelectedEntities = state.entities.filter(entity => entity.checked).length;
            if (numSelectedEntities <= 2) {
                dispatch({ type: AliceGPTEvents.BACK });
            }
            dispatch({
                type: AliceGPTEvents.TOGGLE_DISPLAY_CONFIGURATION_LOADED
            })
        }
        dispatch({ type: AliceGPTEvents.BACK })
    }

    function toggleCreatingProject() {
        setCreatingProject(!creatingProject);
    }

    const handleSubmit = async () => {
        // toggleCreatingProject();

        const organizationDetails = getOrganizationDetails();
        const projectProperties = getProjectProperties();
        const userProperties = getUserProperties();

        const selectedEntities = state.entities.length === 0 || state.step === 0 ? [USER_PROFILE_ENTITY] : getSelectedEntities();
        const selectedRelations = state.entities.length === 0 || state.step < 2 ? [] : getSelectedRelations();

        const entitiesAndAttributes = selectedEntities.map((entity) => {
            const pageConfiguration = state.step === 3 || entity.isUserEntity ? entity.pageConfiguration : DEFAULT_PAGE_CONFIGURATION;
            return {
                name: entity.name?.trim(),
                icon: entity.icon,
                isUserEntity: entity.isUserEntity,
                attributes: entity.attributes.map((attribute) => {
                    let picklist = attribute.picklist;
                    if (attribute.type === "State Field" && attribute.picklist.length === 0) {
                        picklist = ["Not Started", "In Progress", "Completed"];
                    }
                    return {
                        name: attribute.name?.trim(),
                        type: attribute.type,
                        picklist: picklist,
                        required: attribute.type === "State Field" ? true : attribute.required,
                        unique: attribute.unique,
                        isAzureAttribute: attribute.isAzureAttribute,
                        sysName: attribute.sysName
                    }
                }),
                pageConfiguration
            }
        });

        const request = new CreateProjectRequest(
            organizationDetails, projectProperties, userProperties,
            entitiesAndAttributes, selectedRelations
        );

        // const selectedEntities = state.entities.length === 0 ? [USER_PROFILE_ENTITY] : getSelectedEntities();
        // const selectedRelations = state.entities.length === 0 ? [] : getSelectedRelations();

        // await addSelectedDataToProject(
        //     organizationId, project._id, selectedEntities, selectedRelations
        // );

        onCreate(request);
    }

    async function skipRecommendationAndCreateProject() {
        // toggleCreatingProject();
        postMessageToRuntime({
            eventType: "PROJECT_NAME_CHANGED",
            value: state.form.projectName
        })
        const organizationDetails = getOrganizationDetails();
        const projectProperties = getProjectProperties();
        const userProperties = getUserProperties();

        const selectedEntities = [USER_PROFILE_ENTITY];
        const selectedRelations = [];

        const entitiesAndAttributes = selectedEntities.map((entity) => ({
            name: entity.name?.trim(),
            icon: entity.icon,
            isUserEntity: entity.isUserEntity,
            attributes: entity.attributes.map((attribute) => ({
                name: attribute.name?.trim(),
                type: attribute.type,
                picklist: attribute.picklist,
                required: attribute.type === "State Field" ? true : attribute.required,
                unique: attribute.unique,
                isAzureAttribute: attribute.isAzureAttribute,
                sysName: attribute.sysName
            })),
            pageConfiguration: entity.pageConfiguration
        }));

        const request = new CreateProjectRequest(
            organizationDetails, projectProperties, userProperties,
            entitiesAndAttributes, selectedRelations
        );

        skipAIAndCreateProject(request);
    }

    function getOrganizationDetails() {
        const organizationDetails = { organizationId, organizationName };
        return organizationDetails;
    }

    function getProjectProperties() {
        return {
            ...state.form,
            language: state.form.language ? state.form.language.code : "en"
        };
    }

    function getUserProperties() {
        const email = user.username ? user.username : user.emails[0];
        const userProperties = { email };
        return userProperties;
    }

    function getSelectedEntities() {
        return state.entities.filter(entity => entity.checked).map(entity => ({
            ...entity,
            attributes: entity.attributes.filter(attribute => {
                return attribute.selected
            })
        }));
    }

    function getSelectedRelations() {
        return state.relations.filter(relation => relation.selected);
    }

    function showActions() {
        if (state.step === 2) {
            if (creatingProject) return false;
            const selectedEntities = state.entities.filter(entity => entity.checked);
            return selectedEntities.length > 1 ? state.relationsLoaded : true;
        }
        return !creatingProject;
    }

    // function isThereEntityWithDuplicateNames() {
    //     const selectedEntities = state.entities.filter(entity => entity.checked);
    //     return selectedEntities.some((entity, entityIndex) => {
    //         const entityName = entity.name.trim().toLowerCase();
    //         return selectedEntities.some((e, i) =>
    //             e.name.trim().toLowerCase() === entityName &&
    //             i !== entityIndex
    //         );
    //     });
    // }

    const stepContent = [
        <AliceProjectDetailsForm
            isUsedInRuntime={isUsedInRuntime}
            showAIFields={showAIFields}
            showLanguageDropdown={showLanguageDropdown}
            checkDuplicateProjectName={isProjectNameUsed}
        />,
        <AliceRecommendedEntities />,
        <AliceRecommendedRelations />,
        <EditPageConfiguration
            useAI={showAIFields}
        />
    ]

    const stepDialogActions = [
        <div>
            <div className='mx-3 d-flex justify-content-between'>
                {
                    showAIFields ?
                        <>
                            <div>
                                {showBackButton &&
                                    <button className='btn btn-outline-secondary' onClick={backToAppLibrary}>
                                        {t("buttons.back_to_Library")}
                                    </button>
                                }
                            </div>
                            <div>
                                <button className='btn btn-outline-primary' onClick={skipRecommendationAndCreateProject} disabled={isNextDisabled()}>
                                    {t("buttons.skip_and_create_now")}
                                </button>
                                <button className='btn btn-primary ms-2' autoFocus={true} onClick={handleNext} disabled={isNextDisabled()}>
                                    {t("buttons.next")}
                                </button>
                            </div>
                        </>
                        :
                        <button
                            style={{ position: "relative", left: "90%" }}
                            className='btn btn-primary'
                            onClick={handleSubmit}
                            disabled={isNextDisabled()}
                        >
                            {t("buttons.create_app")}
                        </button>
                }
            </div>
        </div >,
        <div>
            <div className='mx-3 d-flex justify-content-between'>
                {
                    state.isLoadingEntities ?
                        <>
                            <div>
                                <button className='btn btn-outline-secondary' onClick={handleBack}>
                                    {t("buttons.back")}
                                </button>
                            </div>
                            <div>
                                <button className='btn btn-primary ms-2' autoFocus={true} onClick={handleNext} disabled>
                                    {t("buttons.next")}
                                </button>
                            </div>
                        </>
                        :
                        <>
                            <div>
                                <button className='btn btn-outline-secondary' onClick={handleBack}>
                                    {t("buttons.back")}
                                </button>
                            </div>
                            <div>
                                {/* {
                                    state.entities.some(entity => (
                                        entity.attributes.filter(attribute => (
                                            attribute.selected && attribute.type === "State Field"
                                        )).length > 1
                                    )) &&
                                    <span className='text-danger'>
                                        {t("errors.only_one_state_allowed")}
                                    </span>
                                } */}
                                {/* {
                                    isThereEntityWithDuplicateNames() &&
                                    <span className='text-danger'>
                                        {t("errors.duplicate_entity_names")}
                                    </span>
                                } */}
                                &nbsp;&nbsp;
                                <button className='btn btn-outline-primary' disabled={isCreateAppDisabled()} onClick={handleSubmit}>
                                    {t("buttons.create_app")}
                                </button>
                                <button className='btn btn-primary ms-2' autoFocus={true} onClick={handleNext} disabled={isNextDisabled()}>
                                    {t("buttons.next")}
                                </button>
                            </div>
                        </>
                }
            </div>
        </div>,
        <div>
            <div className='mx-3 d-flex justify-content-between'>
                <div>
                    <button className='btn btn-outline-secondary' onClick={handleBack}>
                        {t("buttons.back")}
                    </button>
                </div>
                <div>
                    <button className='btn btn-outline-primary' onClick={handleSubmit} disabled={isCreateAppDisabled()}>
                        {t("buttons.create_app")}
                    </button>
                    <button className='btn btn-primary ms-2' autoFocus={true} onClick={handleNext} disabled={isNextDisabled()}>
                        {t("buttons.next")}
                    </button>
                </div>
            </div>
        </div>,
        <div>
            {
                !state.isPageConfigurationLoading &&
                <div className='mx-3 d-flex justify-content-between'>
                    <div>
                        <button className='btn btn-outline-secondary' onClick={handleBack}>
                            {t("buttons.back")}
                        </button>
                    </div>
                    <div>
                        <button className='btn btn-primary' onClick={handleSubmit} disabled={isCreateAppDisabled()}>
                            {t("buttons.create_app")}
                        </button>
                    </div>
                </div>
            }
        </div>
    ]

    return (
        <>
            <div style={{ height: "90%" }}>
                {
                    creatingProject ?
                        <div className="d-flex flex-column justify-content-enter align-items-center w-100 h-100">
                            <div style={{ height: "80%" }}>
                                <img
                                    alt="loader-clone"
                                    height={"100%"}
                                    src={`${process.env.REACT_APP_DATALAKESTORAGE_URL}/${process.env.REACT_APP_DATALAKESTORAGE_RESOURCES}/images/loaders/library.gif`} />
                            </div>
                            <ShuffleMessage
                                data={messagesWhenCreatingProject}
                                interval={3000} />
                        </div>
                        :
                        <Scrollable>
                            {stepContent[state.step]}
                        </Scrollable>
                }
            </div>
            <div className='mt-2'>
                {
                    showActions() && stepDialogActions[state.step]
                }
            </div>
        </>
    )
}

export default AliceGPTDialog;